import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users,
  Crown,
  Trophy,
  Plus,
  X,
  UserPlus,
  LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/imageUtils';

const API = process.env.REACT_APP_BACKEND_URL;

export const TeamCard = ({ team, challenge, api, currentUserId, onUpdate }) => {
  const [joining, setJoining] = useState(false);
  
  const isInTeam = team.members?.some(m => m.user_id === currentUserId);
  const isCaptain = team.captain_id === currentUserId;
  
  const handleJoin = async () => {
    setJoining(true);
    try {
      await api().post(`/challenges/${challenge.id}/teams/${team.id}/join`);
      toast.success(`Joined team "${team.name}"!`);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join team');
    }
    setJoining(false);
  };

  const handleLeave = async () => {
    try {
      await api().post(`/challenges/${challenge.id}/teams/${team.id}/leave`);
      toast.success('Left team');
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error('Failed to leave team');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-card border overflow-hidden ${isInTeam ? 'border-primary' : 'border-border'}`}
    >
      {isInTeam && (
        <div className="bg-primary text-black text-center py-1 text-sm font-medium">
          Your Team
        </div>
      )}
      
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-heading text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              {team.name}
            </h3>
            {team.description && (
              <p className="text-sm text-muted-foreground mt-1">{team.description}</p>
            )}
          </div>
          <Badge className="bg-white/10 text-white">
            {team.member_count}/{challenge.max_team_size || 5} members
          </Badge>
        </div>

        {/* Team Progress */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted-foreground">Team Progress</span>
            <span className="text-primary font-medium">
              {team.total_progress} / {team.team_target}
            </span>
          </div>
          <Progress value={team.progress_percentage} className="h-2" />
        </div>

        {/* Members Preview */}
        <div className="flex items-center gap-1 mb-4">
          {team.members?.slice(0, 5).map((member, i) => (
            <Avatar key={member.user_id} className="h-8 w-8 border-2 border-card -ml-2 first:ml-0">
              <AvatarImage src={getImageUrl(member.user?.profile_picture)} />
              <AvatarFallback className="bg-primary/20 text-primary text-xs">
                {member.user?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
          ))}
          {team.member_count > 5 && (
            <span className="text-xs text-muted-foreground ml-2">+{team.member_count - 5} more</span>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Link to={`/challenges/${challenge.id}/team/${team.id}`} className="flex-1">
            <Button variant="outline" className="w-full">View Team</Button>
          </Link>
          {!isInTeam && team.member_count < (challenge.max_team_size || 5) && (
            <Button 
              onClick={handleJoin}
              disabled={joining}
              className="bg-primary text-black"
            >
              <UserPlus className="h-4 w-4 mr-1" />
              Join
            </Button>
          )}
          {isInTeam && !isCaptain && (
            <Button 
              variant="outline"
              onClick={handleLeave}
              className="text-red-400 border-red-400/50"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const CreateTeamModal = ({ isOpen, onClose, challengeId, api, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a team name');
      return;
    }

    setCreating(true);
    try {
      await api().post(`/challenges/${challengeId}/teams`, { name, description });
      toast.success('Team created!');
      setName('');
      setDescription('');
      onClose();
      if (onCreated) onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create team');
    }
    setCreating(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-card border border-border p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-xl text-white uppercase tracking-wider flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Create Team
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground">Team Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter team name..."
              className="mt-1 bg-black border-border text-white"
            />
          </div>
          
          <div>
            <label className="text-sm text-muted-foreground">Description (optional)</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Team motto or description..."
              className="mt-1 bg-black border-border text-white min-h-[80px]"
            />
          </div>

          <Button
            onClick={handleCreate}
            disabled={!name.trim() || creating}
            className="w-full bg-primary text-black"
          >
            {creating ? 'Creating...' : 'Create Team'}
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export const TeamLeaderboard = ({ teams, challenge, currentUserId }) => {
  return (
    <div className="bg-card border border-border">
      <div className="p-4 border-b border-border">
        <h3 className="font-heading text-lg text-white uppercase tracking-wider flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-400" />
          Team Leaderboard
        </h3>
      </div>
      
      <div className="divide-y divide-border">
        {teams.map((team, index) => {
          const isUserTeam = team.members?.some(m => m.user_id === currentUserId);
          
          return (
            <div 
              key={team.team_id || team.id}
              className={`p-4 flex items-center gap-4 ${isUserTeam ? 'bg-primary/10' : ''}`}
            >
              {/* Rank */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                team.rank === 1 ? 'bg-yellow-500 text-black' :
                team.rank === 2 ? 'bg-gray-400 text-black' :
                team.rank === 3 ? 'bg-orange-600 text-white' :
                'bg-white/10 text-white'
              }`}>
                {team.rank}
              </div>
              
              {/* Team Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  <span className="text-white font-medium">{team.team_name}</span>
                  {team.completed && (
                    <Badge className="bg-green-500/20 text-green-400 text-xs">Done</Badge>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{team.member_count} members</span>
              </div>
              
              {/* Progress */}
              <div className="text-right">
                <div className="text-white font-medium">{team.total_progress}</div>
                <div className="text-xs text-muted-foreground">{team.progress_percentage}%</div>
              </div>
            </div>
          );
        })}
        
        {teams.length === 0 && (
          <div className="p-8 text-center text-muted-foreground">
            No teams yet. Be the first to create one!
          </div>
        )}
      </div>
    </div>
  );
};
